import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Search, MapPin, UtensilsCrossed, ArrowRight, CarTaxiFront, ChefHat, Bike, Star,
  Menu as MenuIcon, X, Locate, Loader2, ShieldCheck, LogOut, LayoutDashboard, ClipboardList,
} from "lucide-react";
import { API } from "../lib/api";
import api from "../lib/api";
import { mediaUrl } from "../lib/mediaUrl";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { isOpenNow } from "../lib/openingHours";
import SEOHead from "../components/SEOHead";
import OpenFastBadge from "../components/OpenFastBadge";

// Catégories icônes cliquables — food-first, aspect moderne
const CATEGORIES = [
  { key: "all", label: "Tous", emoji: "🍽️" },
  { key: "burger", label: "Burgers", emoji: "🍔" },
  { key: "pizza", label: "Pizza", emoji: "🍕" },
  { key: "sushi", label: "Sushi", emoji: "🍣" },
  { key: "italien", label: "Italien", emoji: "🍝" },
  { key: "asiat", label: "Asiatique", emoji: "🥢" },
  { key: "africain", label: "Africain", emoji: "🍛" },
  { key: "libanais", label: "Libanais", emoji: "🥙" },
  { key: "vegan", label: "Végé", emoji: "🥗" },
  { key: "brasserie", label: "Brasserie", emoji: "🍺" },
  { key: "dessert", label: "Desserts", emoji: "🍰" },
];

// Menu hamburger (secondaires) — accessibles mais hors flux principal
const MENU_LINKS = [
  { path: "/restaurateur/inscription", icon: ChefHat, label: "Devenir restaurateur", desc: "Rejoignez le réseau Nold Go" },
  { path: "/auth?role=driver", icon: CarTaxiFront, label: "Travailler avec nous", desc: "Chauffeur VTC · Livreur" },
  { path: "/passenger", icon: MapPin, label: "Commander un VTC", desc: "Trajet voiture · Bruxelles" },
];

// Liens réservés aux utilisateurs connectés selon leur rôle
const ROLE_LINKS = {
  admin: [
    { path: "/admin", icon: LayoutDashboard, label: "Espace Admin", desc: "Tableau de bord & pilotage", accent: true },
  ],
  restaurateur: [
    { path: "/restaurateur", icon: ChefHat, label: "Espace Restaurateur", desc: "Menus, commandes, statistiques", accent: true },
  ],
  driver: [
    { path: "/driver", icon: CarTaxiFront, label: "Espace Chauffeur", desc: "Vos courses & revenus", accent: true },
  ],
};

// ------- Composants ---------

function RestoCard({ r }) {
  // Distance / ETA fournis par /api/restaurants?lat=&lng= si adresse client passée en URL.
  const hasDistance = typeof r.distance_km === "number";
  const etaMin = typeof r.delivery_eta_min === "number" ? r.delivery_eta_min : 30;
  return (
    <Link
      to={`/restaurants/${r.id}`}
      data-testid={`food-resto-card-${r.id}`}
      className="group bg-white rounded-2xl border border-[#E2DFD8] overflow-hidden hover:border-[#059669] hover:shadow-xl transition-all"
    >
      <div className="aspect-[16/10] bg-gradient-to-br from-[#F0EBE1] to-[#E2DFD8] overflow-hidden relative">
        {r.cover_url ? (
          <img
            src={mediaUrl(r.cover_url)}
            alt={r.name}
            className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#75807A]">
            <UtensilsCrossed className="w-10 h-10" />
          </div>
        )}
        <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#059669] text-white text-[10px] font-black uppercase tracking-wider shadow-md">
          <Bike className="w-3 h-3" /> Livraison
        </div>
        {/* Badge FOMO "Ouvert & rapide" (bas-droit) */}
        <div className="absolute bottom-3 right-3">
          <OpenFastBadge resto={r} />
        </div>
        {r.cuisine && (
          <div className="absolute bottom-3 left-3 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[#059669] text-xs font-bold shadow-sm">
            {r.cuisine}
          </div>
        )}
        {hasDistance && (
          <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[#1A1A1A] text-[11px] font-bold shadow-sm" data-testid={`food-resto-distance-${r.id}`}>
            <MapPin className="w-3 h-3 text-[#059669]" /> {r.distance_km.toFixed(1)} km
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-head font-bold text-[#1A1A1A] text-lg truncate">{r.name}</h3>
          <span className="shrink-0 inline-flex items-center gap-1 text-xs font-bold text-[#059669] whitespace-nowrap">
            <Star className="w-3.5 h-3.5 fill-[#059669]" /> 4.6
          </span>
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-xs text-[#75807A]">
          <MapPin className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate">{r.commune || r.address}</span>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs">
          <span className="text-[#75807A]">Livraison ~{etaMin} min · Frais 2,50 €</span>
          <span className="inline-flex items-center gap-1 font-bold text-[#059669] group-hover:translate-x-1 transition-transform">
            Commander <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function CardSkeleton() {
  return (
    <div className="bg-white rounded-2xl border border-[#E2DFD8] overflow-hidden animate-pulse">
      <div className="aspect-[16/10] bg-[#F0EBE1]" />
      <div className="p-4 space-y-2">
        <div className="h-5 bg-[#F0EBE1] rounded w-3/4" />
        <div className="h-3 bg-[#F0EBE1] rounded w-1/2" />
      </div>
    </div>
  );
}

// Bandeau "Nos livreurs" — 2 photos par défaut (seed vrai livreur + livreur généré)
// + jusqu'à 10 photos réelles des 10 premiers livreurs Nold Go approuvés (endpoint public).
function NoldDriversStrip() {
  const [drivers, setDrivers] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get("/nold/showcase-drivers")
      .then((r) => { if (alive) setDrivers((r.data?.items || []).slice(0, 10)); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  // Seed = 5 photos par défaut (diverses origines), toujours affichées en premier
  const seeds = [
    { url: "/nold-driver-real.jpg", alt: "Livreur Nold Go" },
    { url: "/nold-driver-white.jpg?v=2", alt: "Livreur Nold Go" },
    { url: "/nold-driver-3.jpg", alt: "Livreuse Nold Go" },
    { url: "/nold-driver-4.jpg", alt: "Livreur Nold Go" },
    { url: "/nold-driver-5.jpg", alt: "Livreuse Nold Go" },
  ];
  const all = [...seeds, ...drivers.map((d) => ({ url: mediaUrl(d.photo_url), alt: `Livreur ${d.first_name}` }))];
  const count = seeds.length + drivers.length;
  return (
    <section data-testid="nold-drivers-strip" className="max-w-6xl mx-auto px-4 sm:px-6 mt-10">
      <div className="rounded-2xl bg-white border border-[#E2DFD8] p-4 sm:p-5 flex items-center gap-4 sm:gap-5">
        <div className="flex -space-x-3 shrink-0">
          {all.slice(0, 8).map((p, i) => (
            <img key={i} src={p.url} alt={p.alt} className="h-14 w-14 sm:h-16 sm:w-16 rounded-full object-cover border-2 border-white shadow-sm bg-[#F0EBE1]" loading="lazy" />
          ))}
          {all.length > 8 && (
            <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-full border-2 border-white shadow-sm bg-[#059669] text-white flex items-center justify-center text-xs font-black">
              +{all.length - 8}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest font-black text-[#059669]">Nos livreurs · Bruxelles {count > seeds.length && <span className="ml-1 text-[#75807A]">· {count} actifs</span>}</div>
          <div className="mt-0.5 font-head font-bold text-[#1A1A1A] text-sm sm:text-base leading-tight">Livrés par des humains, formés et équipés — pas par des robots.</div>
        </div>
      </div>
    </section>
  );
}

// Menu hamburger latéral (drawer)
function HamburgerMenu({ open, onClose }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const roleLinks = (user && ROLE_LINKS[user.role]) || (user?.is_super_admin ? ROLE_LINKS.admin : []);
  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 backdrop-blur-sm z-50 transition-opacity ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
        data-testid="menu-backdrop"
      />
      {/* Drawer */}
      <aside
        data-testid="hamburger-menu"
        className={`fixed top-0 right-0 h-screen w-full max-w-sm bg-white z-50 shadow-2xl transition-transform duration-300 flex flex-col ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="p-5 flex items-center justify-between border-b border-[#E2DFD8]">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#059669] to-[#047857] flex items-center justify-center">
              <UtensilsCrossed className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-head font-black text-lg leading-tight">Nold Go</div>
              {user && <div className="text-[10px] text-[#75807A] uppercase tracking-wider">{user.name || user.email}</div>}
            </div>
          </div>
          <button
            data-testid="menu-close"
            onClick={onClose}
            className="w-10 h-10 rounded-full hover:bg-[#F0EBE1] flex items-center justify-center"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-2 flex-1 overflow-y-auto">
          {/* Espace utilisateur connecté (Admin / Restaurateur / Chauffeur) — mis en avant */}
          {roleLinks.map((l) => (
            <button
              key={l.path}
              data-testid={`menu-link-${l.path.replace(/[^a-z0-9]/gi, "-")}`}
              onClick={() => { onClose(); navigate(l.path); }}
              className="w-full text-left flex items-center gap-4 p-4 rounded-2xl border-2 border-[#F26419] bg-gradient-to-br from-[#FEF3EC] to-white hover:shadow-md transition-all group"
            >
              <div className="w-12 h-12 rounded-xl bg-[#F26419] flex items-center justify-center shrink-0">
                <l.icon className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="font-head font-bold text-[#1A1A1A]">{l.label}</div>
                <div className="text-xs text-[#75807A] mt-0.5">{l.desc}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-[#F26419] group-hover:translate-x-1 transition-transform" />
            </button>
          ))}
          {roleLinks.length > 0 && <div className="h-px bg-[#E2DFD8] my-3" />}
          {/* Liens secondaires (grand public) */}
          {MENU_LINKS.map((l) => (
            <button
              key={l.path}
              data-testid={`menu-link-${l.path.replace(/[^a-z0-9]/gi, "-")}`}
              onClick={() => { onClose(); navigate(l.path); }}
              className="w-full text-left flex items-center gap-4 p-4 rounded-2xl border-2 border-[#E2DFD8] hover:border-[#059669] hover:bg-[#F0FDF4] transition-all group"
            >
              <div className="w-12 h-12 rounded-xl bg-[#F0FDF4] group-hover:bg-[#059669] flex items-center justify-center transition-colors">
                <l.icon className="w-6 h-6 text-[#059669] group-hover:text-white transition-colors" />
              </div>
              <div className="flex-1">
                <div className="font-head font-bold text-[#1A1A1A]">{l.label}</div>
                <div className="text-xs text-[#75807A] mt-0.5">{l.desc}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-[#75807A] group-hover:text-[#059669] group-hover:translate-x-1 transition-all" />
            </button>
          ))}
          {/* Lien Espace Équipe (agents) — accès permanent quel que soit l'user connecté */}
          <button
            data-testid="menu-link-agent"
            onClick={() => { onClose(); navigate("/equipe"); }}
            className="w-full text-left flex items-center gap-4 p-3 rounded-xl hover:bg-[#F0EBE1] transition-all"
          >
            <ShieldCheck className="w-5 h-5 text-[#75807A]" />
            <span className="text-sm text-[#3B423E] font-semibold">Espace Équipe</span>
          </button>
          {/* Mes commandes — visible pour tout le monde, redirige vers /auth si non connecté */}
          <button
            data-testid="menu-my-orders"
            onClick={() => { onClose(); navigate(user ? "/mes-commandes" : "/auth?role=passenger&intent=orders"); }}
            className="w-full text-left flex items-center gap-4 p-3 rounded-xl bg-[#F0FDF4] hover:bg-[#DCFCE7] transition-all border border-[#059669]/20"
          >
            <ClipboardList className="w-5 h-5 text-[#059669]" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-[#1A1A1A] font-bold">📋 Mes commandes</div>
              <div className="text-[11px] text-[#75807A]">{user ? "Historique de vos commandes" : "Créez un compte ou connectez-vous"}</div>
            </div>
          </button>
          {/* Connexion — visible seulement si personne n'est connecté */}
          {!user && (
            <button
              data-testid="menu-login"
              onClick={() => { onClose(); navigate("/auth"); }}
              className="w-full text-left flex items-center gap-4 p-3 rounded-xl bg-[#F0EBE1] hover:bg-[#E2DFD8] transition-all"
            >
              <LayoutDashboard className="w-5 h-5 text-[#1A1A1A]" />
              <span className="text-sm text-[#1A1A1A] font-bold">Se connecter / Créer un compte</span>
            </button>
          )}
          {/* Déconnexion si connecté */}
          {user && (
            <button
              data-testid="menu-logout"
              onClick={() => { onClose(); logout?.(); navigate("/"); }}
              className="w-full text-left flex items-center gap-4 p-3 rounded-xl hover:bg-[#FEE2E2] transition-all"
            >
              <LogOut className="w-5 h-5 text-[#DC2626]" />
              <span className="text-sm text-[#DC2626] font-semibold">Déconnexion</span>
            </button>
          )}
        </div>
        <div className="p-5 border-t border-[#E2DFD8] bg-[#FDF8F0]">
          <p className="text-xs text-[#75807A] leading-relaxed">
            <strong className="text-[#1A1A1A]">Nold Go SRL</strong><br />
            Rue Jourdan 71 A0, 1060 Saint-Gilles<br />
            TVA · BE 1040.580.267
          </p>
        </div>
      </aside>
    </>
  );
}

// ------- Page principale ---------

export default function NoldFoodHome() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Contexte d'adresse client passé depuis HomeAddress (/) → utilisé pour distance/ETA côté restos.
  const addrParam = searchParams.get("addr") || "";
  const latParam = searchParams.get("lat");
  const lngParam = searchParams.get("lng");
  const catParam = searchParams.get("cat") || "all";
  const [restos, setRestos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState(catParam);
  const [sortBy, setSortBy] = useState("recommended"); // recommended | eta | distance
  const [openOnly, setOpenOnly] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locLabel, setLocLabel] = useState("Bruxelles");

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        // Passe lat/lng si adresse client fournie → chaque resto est enrichi avec distance_km + delivery_eta_min.
        const geoParams = latParam && lngParam ? `&lat=${latParam}&lng=${lngParam}` : "";
        const r = await api.get(`/restaurants?q=${encodeURIComponent(q)}&limit=48${geoParams}`).then(x => x.data);
        if (!cancelled) setRestos(r.items || []);
      } catch (e) { console.warn("Restos fetch failed:", e); }
      finally { if (!cancelled) setLoading(false); }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, latParam, lngParam]);

  // Restaurants en vedette (max 3), chargés une seule fois
  useEffect(() => {
    /* section vedette retirée — la page /restaurants n'affiche que la liste + catégories + filtres */
  }, []);

  // Détection géolocalisation (adresse humaine via reverse-geocoding OSM)
  const detectLocation = () => {
    if (!("geolocation" in navigator)) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&zoom=14&addressdetails=1`);
        const d = await r.json();
        const town = d?.address?.suburb || d?.address?.village || d?.address?.town || d?.address?.city || d?.address?.municipality;
        if (town) setLocLabel(town);
      } catch { /* ignore */ }
      finally { setLocating(false); }
    }, () => setLocating(false), { timeout: 8000 });
  };

  const filtered = useMemo(() => {
    let list = restos;
    if (activeCat !== "all") {
      list = list.filter(r => (r.cuisine || "").toLowerCase().includes(activeCat));
    }
    if (openOnly) {
      // Statut `null` = horaires non renseignés → on garde le resto (on ne pénalise pas ceux sans data)
      list = list.filter(r => isOpenNow(r.opening_hours_text) !== false);
    }
    // Tri appliqué après les filtres
    if (sortBy === "eta") {
      list = [...list].sort((a, b) => (a.delivery_eta_min ?? 30) - (b.delivery_eta_min ?? 30));
    } else if (sortBy === "distance") {
      const has = (x) => typeof x?.distance_km === "number";
      list = [...list].sort((a, b) => {
        if (has(a) && has(b)) return a.distance_km - b.distance_km;
        if (has(a)) return -1;
        if (has(b)) return 1;
        return 0;
      });
    }
    return list;
  }, [restos, activeCat, sortBy, openOnly]);

  const hasGeo = !!(latParam && lngParam);

  return (
    <div className="min-h-screen bg-[#FDF8F0]">
      <SEOHead
        title="Commander à manger à Bruxelles — Nold Go Food"
        description="Nold Go Food : commandez auprès des restaurants bruxellois et faites-vous livrer à vélo. Burgers, sushis, pizza, cuisine du monde — livraison rapide en Belgique."
        canonical="/restaurants"
        type="website"
      />
      {/* NAV TOP compacte : logo bi-services + hamburger */}
      <header data-testid="food-topnav" className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#E2DFD8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/nold-logo.png" alt="Nold Go Food" className="h-9 w-9 object-contain" loading="eager" />
            <span className="font-head font-black text-lg text-[#1A1A1A] leading-none">
              Nold Go
              <span className="ml-1 text-[#059669]">Food</span>
              <span className="mx-1 text-[#75807A]/40">·</span>
              <span className="text-[#F26419]">VTC</span>
            </span>
          </Link>
          <button
            data-testid="hamburger-btn"
            onClick={() => setMenuOpen(true)}
            className="w-11 h-11 rounded-full hover:bg-[#F0EBE1] flex items-center justify-center transition-colors"
            aria-label="Menu"
          >
            <MenuIcon className="w-6 h-6 text-[#1A1A1A]" />
          </button>
        </div>
      </header>

      <HamburgerMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

      {/* Bandeau adresse contexte — visible seulement si l'utilisateur vient de la page d'accueil avec une adresse */}
      {addrParam && (
        <div className="bg-[#F0FDF4] border-b border-[#059669]/20" data-testid="food-addr-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
            <MapPin className="w-4 h-4 text-[#059669] shrink-0" />
            <div className="flex-1 min-w-0 text-sm">
              <span className="text-[#75807A]">Livraison à </span>
              <span className="font-bold text-[#1A1A1A] truncate">{addrParam}</span>
            </div>
            <button
              data-testid="food-addr-change"
              onClick={() => navigate("/")}
              className="shrink-0 inline-flex items-center gap-1 h-8 px-3 rounded-full bg-white border border-[#059669] text-[#059669] hover:bg-[#DCFCE7] text-xs font-bold transition-colors"
            >
              Modifier
            </button>
          </div>
        </div>
      )}

      {/* Barre de recherche + géolocalisation — en tête de liste */}
      <section data-testid="food-search-section" className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 sm:pt-10 pb-4">
        <div className="text-center max-w-3xl mx-auto mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F0FDF4] border border-[#059669]/30 text-[#059669] text-xs font-bold uppercase tracking-widest">
            <Bike className="w-3.5 h-3.5" /> Livraison en 30 min · Bruxelles
          </div>
          <h1 className="mt-4 font-head text-2xl sm:text-3xl lg:text-4xl font-black text-[#1A1A1A] leading-[1.05]">
            Restaurants à <span className="text-[#059669]">Bruxelles</span>
          </h1>
        </div>

        {/* Recherche + Localisation */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl border-2 border-[#E2DFD8] shadow-sm overflow-hidden flex flex-col sm:flex-row focus-within:border-[#059669] transition-colors">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 text-[#75807A] absolute left-4 top-1/2 -translate-y-1/2 z-10" />
              <Input
                data-testid="food-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Que voulez-vous manger ?"
                className="h-14 pl-12 pr-4 border-0 rounded-none bg-transparent text-base focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </div>
            <div className="hidden sm:block w-px bg-[#E2DFD8] my-3" />
            <button
              data-testid="food-locate"
              onClick={detectLocation}
              disabled={locating}
              className="h-14 px-5 flex items-center gap-2 text-sm font-bold text-[#059669] hover:bg-[#F0FDF4] transition-colors border-t sm:border-t-0 border-[#E2DFD8]"
            >
              {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Locate className="w-4 h-4" />}
              <span className="truncate max-w-[120px]">{locLabel}</span>
            </button>
          </div>
        </div>
      </section>

      {/* CATÉGORIES icônes cliquables */}
      <section data-testid="food-categories" className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 pb-2">        <div className="flex gap-3 overflow-x-auto pb-3 -mx-1 px-1 scrollbar-hide snap-x snap-mandatory">
          {CATEGORIES.map(c => (
            <button
              key={c.key}
              data-testid={`food-cat-${c.key}`}
              onClick={() => setActiveCat(c.key)}
              className={`shrink-0 snap-start flex flex-col items-center justify-center gap-1.5 w-20 sm:w-24 h-24 sm:h-28 rounded-2xl transition-all ${
                activeCat === c.key
                  ? "bg-[#059669] text-white shadow-md ring-2 ring-[#059669]/30 ring-offset-2 ring-offset-[#FDF8F0]"
                  : "bg-white border border-[#E2DFD8] text-[#3B423E] hover:border-[#059669] hover:-translate-y-0.5"
              }`}
            >
              <span className="text-3xl sm:text-4xl">{c.emoji}</span>
              <span className="text-xs font-bold">{c.label}</span>
            </button>
          ))}
        </div>
      </section>

      {/* GRILLE RESTAURANTS */}
      <section data-testid="food-restaurants-grid" className="max-w-7xl mx-auto px-4 sm:px-6 pt-4">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-head text-xl sm:text-2xl font-bold text-[#1A1A1A]">
            {activeCat === "all" ? "Restaurants populaires" : `${CATEGORIES.find(c => c.key === activeCat)?.label}`}
            <span className="ml-2 text-sm font-semibold text-[#059669]">({filtered.length})</span>
          </h2>
          {/* Tri — filtres avancés */}
          <div className="flex flex-wrap items-center gap-2" data-testid="food-sort">
            <button
              data-testid="filter-open-now"
              onClick={() => setOpenOnly(v => !v)}
              className={`h-9 px-3 rounded-full text-xs font-bold transition-all inline-flex items-center gap-1 ${
                openOnly
                  ? "bg-[#F26419] text-white shadow-sm"
                  : "bg-white border border-[#E2DFD8] text-[#3B423E] hover:border-[#F26419] hover:text-[#F26419]"
              }`}
              title="Masquer les restaurants fermés"
            >
              <span className={`w-2 h-2 rounded-full ${openOnly ? "bg-white" : "bg-[#F26419]"}`} />
              Ouvert maintenant
            </button>
            <span className="text-xs text-[#75807A] font-semibold hidden sm:inline">· Trier par</span>
            {[
              { key: "recommended", label: "Recommandés" },
              { key: "eta", label: "⚡ Livraison rapide" },
              { key: "distance", label: "📍 Plus proches", requiresGeo: true },
            ].map(s => {
              const disabled = s.requiresGeo && !hasGeo;
              const active = sortBy === s.key;
              return (
                <button
                  key={s.key}
                  data-testid={`sort-${s.key}`}
                  onClick={() => !disabled && setSortBy(s.key)}
                  disabled={disabled}
                  title={disabled ? "Saisissez votre adresse sur l'accueil pour activer" : ""}
                  className={`h-9 px-3 rounded-full text-xs font-bold transition-all ${
                    active
                      ? "bg-[#059669] text-white shadow-sm"
                      : disabled
                        ? "bg-white border border-[#E2DFD8] text-[#C0C6C3] cursor-not-allowed"
                        : "bg-white border border-[#E2DFD8] text-[#3B423E] hover:border-[#059669] hover:text-[#059669]"
                  }`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => <CardSkeleton key={i} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-[#E2DFD8] p-10 text-center text-[#75807A]">
            <UtensilsCrossed className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="font-bold text-[#3B423E]">Aucun restaurant ne correspond.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map(r => <RestoCard key={r.id} r={r} />)}
          </div>
        )}
      </section>

      {/* FOOTER — liens secondaires + légal */}
      <footer data-testid="food-footer" className="bg-white border-t border-[#E2DFD8] mt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="font-head font-bold text-[#1A1A1A] mb-3">Nold Go</h4>
              <div className="space-y-2 text-sm text-[#75807A]">
                <Link to="/" className="block hover:text-[#059669]">Accueil</Link>
                <Link to="/restaurants" className="block hover:text-[#059669]">Tous les restaurants</Link>
                <Link to="/covoiturage/bruxelles-paris" data-testid="footer-carpool-bxl-paris" className="block hover:text-[#059669]">Covoiturage Bruxelles – Paris</Link>
                <Link to="/covoiturage/paris-bruxelles" data-testid="footer-carpool-paris-bxl" className="block hover:text-[#059669]">Covoiturage Paris – Bruxelles</Link>
                <Link to="/mentions-legales" className="block hover:text-[#059669]">À propos</Link>
              </div>
            </div>
            <div>
              <h4 className="font-head font-bold text-[#1A1A1A] mb-3">Rejoignez-nous</h4>
              <div className="space-y-2 text-sm text-[#75807A]">
                <Link to="/restaurateur/inscription" className="block hover:text-[#059669]">Devenir restaurateur</Link>
                <Link to="/auth?role=driver" className="block hover:text-[#059669]">Devenir chauffeur/livreur</Link>
              </div>
            </div>
            <div>
              <h4 className="font-head font-bold text-[#1A1A1A] mb-3">Légal</h4>
              <div className="space-y-2 text-sm text-[#75807A]">
                <Link to="/mentions-legales" className="block hover:text-[#059669]">Mentions légales</Link>
                <Link to="/conditions" className="block hover:text-[#059669]">CGU</Link>
                <Link to="/confidentialite" className="block hover:text-[#059669]">Confidentialité</Link>
              </div>
            </div>
            <div>
              <h4 className="font-head font-bold text-[#1A1A1A] mb-3">Contact</h4>
              <div className="space-y-2 text-sm text-[#75807A]">
                <a href="mailto:contact@noldgo.com" className="block hover:text-[#059669]">contact@noldgo.com</a>
                <a href="tel:+3224646464" className="block hover:text-[#059669]">+32 2 646 46 46</a>
              </div>
            </div>
          </div>
          <div className="pt-6 border-t border-[#E2DFD8] flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#75807A]">
            <p>© {new Date().getFullYear()} <strong className="text-[#1A1A1A]">Nold Go SRL</strong> · Rue Jourdan 71 A0, 1060 Saint-Gilles, Bruxelles · TVA BE 1040.580.267</p>
            <p className="text-[#059669] font-bold">🇧🇪 Fait à Bruxelles</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
